// El nombre de un diseño, editable en el lugar: se toca el nombre (o el lápiz) y se vuelve
// un input; Enter o salir del campo guarda, Escape cancela. Lo usan la cabecera del lienzo
// y las cards de la portada (pedido de Matías del 2026-09-18). El nombre ES el título del
// chat (ADR-2026-09-17-un-diseno-es-un-chat): guardar es `renombrarChat`.

import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Pencil } from 'lucide-react';
import { TITULO_DE_DISENO_MAX, normalizarTituloDeDiseno } from './disenoGuardado';

export const TituloDeDiseno: React.FC<{
  titulo: string;
  /** Guarda el nombre nuevo (ya normalizado); devuelve si salió. Si no, el campo queda abierto. */
  onRenombrar: (titulo: string) => Promise<boolean>;
  /** `cabecera` es el h1 del lienzo; `card`, el pie de una card de la portada. */
  variante: 'cabecera' | 'card';
  /** Lo que va después del nombre cuando no se edita (el rótulo del modo, en la cabecera). */
  sufijo?: React.ReactNode;
}> = ({ titulo, onRenombrar, variante, sufijo }) => {
  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState(titulo);
  const [guardando, setGuardando] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Enter y el blur que sigue al desmontar el campo llegan seguidos: se guarda UNA vez.
  const confirmandoRef = useRef(false);

  useEffect(() => {
    if (!editando) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editando]);

  const empezar = () => {
    setBorrador(titulo);
    setEditando(true);
  };
  const confirmar = async () => {
    if (confirmandoRef.current) return;
    const nuevo = normalizarTituloDeDiseno(borrador);
    if (!nuevo || nuevo === titulo) {
      setEditando(false);
      return;
    }
    confirmandoRef.current = true;
    setGuardando(true);
    try {
      if (await onRenombrar(nuevo)) setEditando(false);
    } finally {
      setGuardando(false);
      confirmandoRef.current = false;
    }
  };

  const letra = variante === 'cabecera' ? 'text-[15px] font-semibold' : 'text-[13px] font-semibold';

  if (editando) {
    return (
      <form
        className="flex min-w-0 flex-1 items-center gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          void confirmar();
        }}
      >
        <input
          ref={inputRef}
          value={borrador}
          maxLength={TITULO_DE_DISENO_MAX}
          disabled={guardando}
          onChange={(e) => setBorrador(e.target.value)}
          onBlur={() => void confirmar()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setEditando(false);
            }
          }}
          aria-label="Nombre del diseño"
          className={`min-w-0 flex-1 rounded-lg border border-[rgb(var(--brand-primary-rgb)_/_0.5)] bg-white px-2 py-1 ${letra} text-brand-black outline-none ring-2 ring-[rgb(var(--brand-primary-rgb)_/_0.15)] disabled:opacity-60`}
        />
        {guardando ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[rgb(var(--brand-black-rgb)_/_0.45)]" aria-hidden="true" />
        ) : (
          <button
            type="submit"
            // Que el click no dispare antes el blur del input (que ya guarda): se guarda una vez.
            onMouseDown={(e) => e.preventDefault()}
            aria-label="Guardar el nombre"
            title="Guardar"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-brand-primary transition-colors hover:bg-[rgb(var(--brand-primary-rgb)_/_0.08)]"
          >
            <Check className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
          </button>
        )}
      </form>
    );
  }

  return (
    <div className="group flex min-w-0 items-center gap-1">
      <button
        type="button"
        onClick={empezar}
        title="Cambiar el nombre"
        className={`min-w-0 truncate rounded-md text-left ${letra} text-brand-black`}
      >
        {titulo}
        {sufijo}
      </button>
      <button
        type="button"
        onClick={empezar}
        aria-label="Cambiar el nombre"
        title="Cambiar el nombre"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[rgb(var(--brand-black-rgb)_/_0.45)] opacity-0 transition-[opacity,background-color,color] hover:bg-[rgb(var(--brand-black-rgb)_/_0.06)] hover:text-brand-black focus-visible:opacity-100 group-hover:opacity-100"
      >
        <Pencil className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
      </button>
    </div>
  );
};
